import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch, useError } from './api';
import { DASHBOARD_QUERY_KEY } from './dashboard';

const normalizeUserIdForApi = (value) => {
  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();

    if (/^\d+$/.test(trimmedValue)) {
      return Number(trimmedValue);
    }

    return trimmedValue;
  }

  return value;
};

export const buildCreateGroupPayload = ({
  groupId = null,
  title,
  facilitatorIds = [],
  parentIds = [],
}) => ({
  groupId:
    groupId === null || groupId === undefined
      ? null
      : normalizeUserIdForApi(groupId),
  title,
  Users: [...new Set([...facilitatorIds, ...parentIds])].map(
    normalizeUserIdForApi,
  ),
});

export const createGroup = async ({ title, facilitatorIds, parentIds }) => {
  const payload = buildCreateGroupPayload({
    groupId: null,
    title,
    facilitatorIds,
    parentIds,
  });
  const response = await apiFetch(
    `${process.env.REACT_APP_API_URL}/api/admin/group`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );

  try {
    return await response.json();
  } catch (_error) {
    return payload;
  }
};

export const updateGroup = async ({
  groupId,
  title,
  facilitatorIds,
  parentIds,
}) => {
  const payload = buildCreateGroupPayload({
    groupId,
    title,
    facilitatorIds,
    parentIds,
  });
  const response = await apiFetch(
    `${process.env.REACT_APP_API_URL}/api/admin/group`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );

  try {
    return await response.json();
  } catch (_error) {
    return payload;
  }
};

export const deleteGroup = async (groupId) => {
  const normalizedGroupId = normalizeUserIdForApi(groupId);
  const response = await apiFetch(
    `${process.env.REACT_APP_API_URL}/api/admin/group/${normalizedGroupId}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  try {
    return await response.json();
  } catch (_error) {
    return { groupId: normalizedGroupId };
  }
};

export const assignUserToGroup = async ({ groupId, userId }) => {
  const normalizedGroupId = normalizeUserIdForApi(groupId);
  const normalizedUserId = normalizeUserIdForApi(userId);
  const response = await apiFetch(
    `${process.env.REACT_APP_API_URL}/api/admin/group/${normalizedGroupId}/user/${normalizedUserId}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  try {
    return await response.json();
  } catch (_error) {
    return {
      groupId: normalizedGroupId,
      userId: normalizedUserId,
    };
  }
};

export const useCreateGroupMutation = (options = {}) => {
  const queryClient = useQueryClient();
  const { checkError } = useError();
  const { onError, onSuccess, ...mutationOptions } = options;

  return useMutation({
    mutationFn: createGroup,
    ...mutationOptions,
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: DASHBOARD_QUERY_KEY,
      });
      onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      checkError(error);
      onError?.(error, variables, context);
    },
  });
};

export const useUpdateGroupMutation = (options = {}) => {
  const queryClient = useQueryClient();
  const { checkError } = useError();
  const { onError, onSuccess, ...mutationOptions } = options;

  return useMutation({
    mutationFn: updateGroup,
    ...mutationOptions,
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: DASHBOARD_QUERY_KEY,
      });
      onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      checkError(error);
      onError?.(error, variables, context);
    },
  });
};

export const useDeleteGroupMutation = (options = {}) => {
  const queryClient = useQueryClient();
  const { checkError } = useError();
  const { onError, onSuccess, ...mutationOptions } = options;

  return useMutation({
    mutationFn: deleteGroup,
    ...mutationOptions,
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: DASHBOARD_QUERY_KEY,
      });
      onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      checkError(error);
      onError?.(error, variables, context);
    },
  });
};

export const useAssignUserToGroupMutation = (options = {}) => {
  const queryClient = useQueryClient();
  const { checkError } = useError();
  const { onError, onSuccess, ...mutationOptions } = options;

  return useMutation({
    mutationFn: assignUserToGroup,
    ...mutationOptions,
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: DASHBOARD_QUERY_KEY,
      });
      onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      checkError(error);
      onError?.(error, variables, context);
    },
  });
};
